import { Button, Divider } from '@fl/cmsch-fe-library';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { Sex } from 'api/gen/Sex';
import { useOurTranslation } from 'app/translations';
import { useForm } from '../../../../utils/forms';
import { breederFemaleDocumentsOptions, bullsDocumentOptions, exportPdfFormName, exportPdfInitialValues } from '../../types/export-pdf-form-values';
interface Props {
  /** Uses the breeding-female document labels when printing cows. */
  isFromCows: boolean;
  /** Shows the breeding-female document select when the table contains females. */
  tableContainsFemales: boolean;
  /** Shows the bull document select when the table contains bulls. */
  tableContainsBulls: boolean;
  /** Submits the selected print document type. */
  onSubmit(sex: Sex): void;
  /** Closes the modal without exporting anything. */
  onCancel(): void;
}
const ExportPdfFormBase: FC<Props> = ({
  onSubmit,
  onCancel,
  isFromCows,
  tableContainsFemales,
  tableContainsBulls
}) => {
  const {
    t,
    tCommon
  } = useOurTranslation('animalBrowser');
  const {
    Form,
    Fields,
    submitting
  } = useForm({
    form: exportPdfFormName,
    initialValues: exportPdfInitialValues,
    destroyOnUnmount: true
  });
  const breederFemaleDocumentsLabel = useMemo(() => t('documentTypeFor', {
    animal: t(isFromCows ? 'breedingFemales' : 'heifers')
  }), [t, isFromCows]);
  const handleSubmitB = useCallback(() => {
    onSubmit('MALE');
  }, [onSubmit]);
  const handleSubmitJ = useCallback(() => {
    onSubmit('FEMALE');
  }, [onSubmit]);
  return <Form data-sentry-element="Form" data-sentry-component="ExportPdfFormBase" data-sentry-source-file="index.tsx">
            {tableContainsFemales && <Fields.StringSelect name="breederFemaleDocuments" label={breederFemaleDocumentsLabel} options={breederFemaleDocumentsOptions} disabled={submitting} />}
            {!isFromCows && tableContainsBulls && <Fields.StringSelect name="bullsDocument" label={t('documentTypeFor', {
      animal: t('bulls')
    })} options={bullsDocumentOptions} disabled={submitting} />}
            <Divider data-sentry-element="Divider" data-sentry-source-file="index.tsx" />
            <div className="row">
                <div className="col-12 d-flex justify-content-end">
                    <div className="mr-2">
                        <Button key="back" type="reset" onClick={onCancel} loading={submitting} testId="cancel-button" disabled={submitting} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                            {tCommon('cancel')}
                        </Button>
                    </div>
                    {tableContainsFemales && <div className="mr-2">
                            <Button key="printJ" visuals="primary" type="button" onClick={handleSubmitJ} loading={submitting} testId="printJ" tooltip={t('printJTooltip')} disabled={submitting}>
                                {isFromCows ? tCommon('print') : t('printJ')}
                            </Button>
                        </div>}
                    {tableContainsBulls && <div className="mr-2">
                            <Button key="printB" visuals="primary" type="button" onClick={handleSubmitB} loading={submitting} testId="printB" tooltip={t('printBTooltip')} disabled={submitting}>
                                {t('printB')}
                            </Button>
                        </div>}
                </div>
            </div>
        </Form>;
};

/**
 * Renders the print/export form used by the basic animal-browser PDF modal.
 *
 * @param props Export context, table content flags, and submit/cancel callbacks.
 * @returns The PDF export form with sex-specific print actions.
 *
 * @example
 * <ExportPdfForm
 *   isFromCows={false}
 *   tableContainsFemales
 *   tableContainsBulls
 *   onSubmit={handleSubmit}
 *   onCancel={handleCancel}
 * />
 */
export const ExportPdfForm = memo(ExportPdfFormBase);